import { useEffect, useMemo } from 'react';
import { CircleMarker, MapContainer, Polygon, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ZONES_BUNDLE,
  REAL_POLY_SOURCES,
  nearestRealL3,
  type ZoneEntry,
} from '@auctionradar/shared';

/**
 * Interactive location map for the Lead detail page. Shows the lead's exact
 * coordinate as a pin, the L3 it sits in (accent outline), and the surrounding
 * L3 neighbourhoods (muted outlines) for spatial context.
 *
 * Built on the same Carto-light tiles + SVG-marker approach as ZoneMiniMap
 * (no broken default-marker-icon issue). Only L3s with real polygons are
 * drawn — synthetic/parent-cloned sources are skipped (drawing them would
 * paint a parent's shape as if it were the neighbourhood).
 */

const CARTO_LIGHT = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const CARTO_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const ACCENT = '#059669';
const MUTED = '#94a3b8';

/** ~degrees radius around the point to pull neighbouring L3s for context. */
const NEAR_DEG = 0.018;

const isReal = (z: ZoneEntry): boolean =>
  !!z.rings && z.rings.length > 0 && REAL_POLY_SOURCES.has(z.polygon_source) && !!z.bbox;

/** Bundle rings are [lng,lat]; Leaflet polygons want [lat,lng]. */
const toLatLng = (ring: Array<[number, number]>): [number, number][] =>
  ring.map(([lng, lat]) => [lat, lng]);

function FitToPoint({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    const d = 0.011;
    map.fitBounds(
      [
        [lat - d, lng - d],
        [lat + d, lng + d],
      ],
      { padding: [16, 16], maxZoom: 16, animate: false },
    );
  }, [lat, lng, map]);
  return null;
}

export function LeadLocationMap({
  lat,
  lng,
  zoneSlug,
}: {
  lat: number | null;
  lng: number | null;
  zoneSlug: string | null;
}) {
  const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

  const { containingSlug, neighbours } = useMemo(() => {
    // The L3 the lead belongs to: its own zoneSlug if real, else nearest.
    let containing: string | null = null;
    if (zoneSlug) {
      const idx = ZONES_BUNDLE.by_slug[zoneSlug];
      const z = idx != null ? ZONES_BUNDLE.zones[idx] : null;
      if (z && isReal(z)) containing = z.slug;
    }
    if (!containing && hasCoords) {
      containing = nearestRealL3(lat as number, lng as number)?.zone.slug ?? null;
    }

    if (!hasCoords) return { containingSlug: containing, neighbours: [] as ZoneEntry[] };

    const near = ZONES_BUNDLE.zones.filter((z) => {
      if (z.level !== 3 || !isReal(z)) return false;
      const [minLng, minLat, maxLng, maxLat] = z.bbox as [number, number, number, number];
      const cLng = (minLng + maxLng) / 2;
      const cLat = (minLat + maxLat) / 2;
      return Math.abs(cLng - (lng as number)) < NEAR_DEG && Math.abs(cLat - (lat as number)) < NEAR_DEG;
    });
    return { containingSlug: containing, neighbours: near };
  }, [zoneSlug, lat, lng, hasCoords]);

  // No coords: fall back to the containing zone's centroid if we have one.
  const fallbackCenter = useMemo<[number, number] | null>(() => {
    if (hasCoords) return [lat as number, lng as number];
    if (containingSlug) {
      const idx = ZONES_BUNDLE.by_slug[containingSlug];
      const z = idx != null ? ZONES_BUNDLE.zones[idx] : null;
      if (z?.centroid) return [z.centroid[1], z.centroid[0]];
    }
    return null;
  }, [hasCoords, lat, lng, containingSlug]);

  if (!fallbackCenter) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-400">
        Δεν υπάρχουν γεωγραφικά δεδομένα για αυτό το ακίνητο.
      </div>
    );
  }

  return (
    <MapContainer
      center={fallbackCenter}
      zoom={15}
      scrollWheelZoom={false}
      className="h-full w-full rounded-lg"
    >
      <TileLayer url={CARTO_LIGHT} attribution={CARTO_ATTR} subdomains="abcd" maxZoom={19} />

      {/* Neighbouring L3s — context. */}
      {neighbours
        .filter((z) => z.slug !== containingSlug)
        .map((z) =>
          z.rings?.map((ring, i) => (
            <Polygon
              key={`${z.slug}-${i}`}
              positions={toLatLng(ring)}
              pathOptions={{ color: MUTED, weight: 1, fill: false, opacity: 0.6 }}
            />
          )),
        )}

      {/* The containing L3 — accent. */}
      {containingSlug &&
        (() => {
          const idx = ZONES_BUNDLE.by_slug[containingSlug];
          const z = idx != null ? ZONES_BUNDLE.zones[idx] : null;
          if (!z || !isReal(z)) return null;
          return z.rings?.map((ring, i) => (
            <Polygon
              key={`containing-${i}`}
              positions={toLatLng(ring)}
              pathOptions={{
                color: ACCENT,
                weight: 2.5,
                fillColor: ACCENT,
                fillOpacity: 0.08,
                lineJoin: 'round',
              }}
            />
          ));
        })()}

      {/* Exact point. */}
      {hasCoords && (
        <CircleMarker
          center={[lat as number, lng as number]}
          radius={6}
          pathOptions={{ color: '#ffffff', weight: 2, fillColor: ACCENT, fillOpacity: 1 }}
        />
      )}

      {hasCoords && <FitToPoint lat={lat as number} lng={lng as number} />}
    </MapContainer>
  );
}
